import { useState } from "react";
import { useLocation } from "wouter";
import { CheckCircle2, ChevronRight, ChevronDown, BookOpen, Minus, PlayCircle } from "lucide-react";
import { useSetupGuide, type SetupStepId } from "@/hooks/use-setup-guide";
import { CHECKLIST_ITEMS, goToChecklistItem } from "@/lib/setup-checklist";
import { tourRefsFrom, tourStartFor } from "@/lib/guided-tour-config";
import { Button } from "@/components/ui/button";

const GREEN = "hsl(142, 71%, 45%)";

const textButton: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: "0.125rem 0",
  cursor: "pointer",
  fontSize: "0.6875rem",
  color: "hsl(var(--muted-foreground))",
  whiteSpace: "nowrap",
};

/**
 * The setup checklist: one list for every new user. Each item ticks itself off
 * from the account's real records (worked out by the server), so it never
 * depends on catching a button click. Items with a walkthrough get a
 * "Show me" button that runs the guided overlay from that point.
 */
export function SetupGuide() {
  const { guideState, isOpen, closeGuide, openGuide, dismissStep, dismissGuide, startTour, clearDemoData } = useSetupGuide();
  const [, navigate] = useLocation();
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  if (!guideState || guideState.dismissed) return null;
  // The walkthrough overlay owns the screen while it runs.
  if (guideState.tourActive) return null;
  // The welcome form is showing (see WelcomeSetup).
  if (!guideState.welcomeDone && !guideState.completedSteps.includes("business-details")) return null;

  const { completedSteps, dismissedSteps, steps } = guideState;
  const handled = (s: SetupStepId) => completedSteps.includes(s) || dismissedSteps.includes(s);
  const completedCount = steps.filter(handled).length;
  const totalCount = steps.length;
  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const allDone = steps.every(handled);
  const refs = tourRefsFrom(guideState);

  function goTo(stepId: SetupStepId) {
    closeGuide();
    goToChecklistItem(CHECKLIST_ITEMS[stepId].href, navigate);
  }

  async function removeSampleData() {
    setClearing(true);
    try {
      await clearDemoData();
      setConfirmClear(false);
    } finally {
      setClearing(false);
    }
  }

  // ── Minimised pill ────────────────────────────────────────────────────────
  if (!isOpen) {
    return (
      <div
        style={{
          position: "fixed",
          bottom: "1.5rem",
          right: "1.5rem",
          zIndex: 50,
        }}
      >
        <button
          onClick={() => openGuide()}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "0.5rem",
            padding: "0.5rem 0.875rem",
            borderRadius: "9999px",
            background: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            boxShadow: "0 4px 16px -4px rgba(0,0,0,0.18)",
            cursor: "pointer",
            color: "hsl(var(--foreground))",
            fontSize: "0.8125rem",
            fontWeight: 500,
            whiteSpace: "nowrap",
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "hsl(var(--accent))"; }}
          onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "hsl(var(--card))"; }}
        >
          <BookOpen style={{ width: "0.875rem", height: "0.875rem", color: "hsl(var(--primary))", flexShrink: 0 }} />
          <span>Get started</span>
          <span style={{
            padding: "0.125rem 0.4rem",
            borderRadius: "9999px",
            background: allDone ? "hsl(142, 71%, 45% / 0.15)" : "hsl(var(--primary) / 0.12)",
            color: allDone ? GREEN : "hsl(var(--primary))",
            fontSize: "0.6875rem",
            fontWeight: 700,
          }}>
            {completedCount}/{totalCount}
          </span>
          <ChevronDown style={{ width: "0.875rem", height: "0.875rem", color: "hsl(var(--muted-foreground))", flexShrink: 0 }} />
        </button>
      </div>
    );
  }

  // ── Expanded panel ────────────────────────────────────────────────────────
  return (
    <div
      style={{
        position: "fixed",
        bottom: "1.5rem",
        right: "1.5rem",
        zIndex: 50,
        width: "22rem",
        maxWidth: "calc(100vw - 2rem)",
        maxHeight: "calc(100vh - 3rem)",
        display: "flex",
        flexDirection: "column",
        background: "hsl(var(--card))",
        borderRadius: "0.75rem",
        border: "1px solid hsl(var(--border))",
        boxShadow: "0 8px 32px -8px rgba(0,0,0,0.22)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div style={{ padding: "1rem 1rem 0.75rem", borderBottom: "1px solid hsl(var(--border))" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.625rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
            <BookOpen style={{ width: "1rem", height: "1rem", color: "hsl(var(--primary))", flexShrink: 0 }} />
            <span style={{ fontSize: "0.9375rem", fontWeight: 700, color: "hsl(var(--foreground))" }}>
              Get started with QuoTrak
            </span>
          </div>
          <button
            onClick={closeGuide}
            title="Minimize"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "1.625rem",
              height: "1.625rem",
              borderRadius: "0.375rem",
              border: "none",
              background: "none",
              cursor: "pointer",
              color: "hsl(var(--muted-foreground))",
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "hsl(var(--accent))"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "none"; }}
          >
            <Minus style={{ width: "0.875rem", height: "0.875rem" }} />
          </button>
        </div>

        {/* Progress bar */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.3rem" }}>
            <span style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))" }}>
              {completedCount} of {totalCount} done
            </span>
            <span style={{ fontSize: "0.6875rem", fontWeight: 600, color: allDone ? GREEN : "hsl(var(--foreground))" }}>
              {progressPct}%
            </span>
          </div>
          <div style={{ height: "0.3125rem", borderRadius: "9999px", background: "hsl(var(--muted))", overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                borderRadius: "9999px",
                background: allDone ? GREEN : "hsl(var(--primary))",
                width: `${progressPct}%`,
                transition: "width 0.4s ease",
              }}
            />
          </div>
        </div>
      </div>

      {/* Items */}
      <div style={{ overflowY: "auto", flex: 1, padding: "0.5rem 0.625rem" }}>
        {steps.map((stepId) => {
          const config = CHECKLIST_ITEMS[stepId];
          if (!config) return null;
          const isComplete = completedSteps.includes(stepId);
          const isDismissed = !isComplete && dismissedSteps.includes(stepId);
          const isOpenItem = !isComplete && !isDismissed;
          const tourStart = tourStartFor(stepId, refs);
          const Icon = config.icon;

          return (
            <div key={stepId} style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", padding: "0.375rem 0" }}>
              <div style={{ flexShrink: 0, paddingTop: "0.125rem" }}>
                {isComplete
                  ? <CheckCircle2 style={{ width: "1.125rem", height: "1.125rem", color: GREEN }} />
                  : <Icon style={{ width: "1.125rem", height: "1.125rem", color: isDismissed ? "hsl(var(--muted-foreground) / 0.4)" : "hsl(var(--muted-foreground))" }} />
                }
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <button
                  onClick={() => { if (isOpenItem) goTo(stepId); }}
                  disabled={!isOpenItem}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.375rem",
                    width: "100%",
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: isOpenItem ? "pointer" : "default",
                    textAlign: "left",
                  }}
                >
                  <span style={{
                    fontSize: "0.8125rem",
                    fontWeight: isOpenItem ? 500 : 400,
                    color: isComplete
                      ? "hsl(var(--muted-foreground))"
                      : isDismissed
                      ? "hsl(var(--muted-foreground) / 0.5)"
                      : "hsl(var(--foreground))",
                    textDecoration: isDismissed ? "line-through" : "none",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}>
                    {config.title}
                  </span>
                  {isOpenItem && (
                    <ChevronRight style={{ width: "0.875rem", height: "0.875rem", color: "hsl(var(--muted-foreground))", flexShrink: 0 }} />
                  )}
                </button>

                {isOpenItem && (
                  <>
                    <div style={{ fontSize: "0.6875rem", color: "hsl(var(--muted-foreground))", lineHeight: 1.4, marginTop: "0.125rem" }}>
                      {config.description}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginTop: "0.375rem" }}>
                      {tourStart !== null && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[0.6875rem] gap-1"
                          onClick={() => void startTour(tourStart)}
                        >
                          <PlayCircle className="w-3 h-3" /> Show me
                        </Button>
                      )}
                      <button
                        onClick={() => void dismissStep(stepId)}
                        style={textButton}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "hsl(var(--foreground))"; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "hsl(var(--muted-foreground))"; }}
                      >
                        Skip
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ padding: "0.625rem 1rem 0.875rem", borderTop: "1px solid hsl(var(--border))", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
        {guideState.hasDemoData && (
          confirmClear ? (
            <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", lineHeight: 1.45 }}>
              Remove the sample customer, products, project and anything made in the walkthrough? Your business details stay.
              <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                <Button size="sm" variant="destructive" className="h-7 text-xs" disabled={clearing} onClick={() => void removeSampleData()}>
                  {clearing ? "Removing…" : "Remove"}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={clearing} onClick={() => setConfirmClear(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <button onClick={() => setConfirmClear(true)} style={{ ...textButton, fontSize: "0.75rem", textAlign: "center", width: "100%" }}>
              Remove sample data
            </button>
          )
        )}
        {allDone ? (
          <Button size="sm" className="w-full" onClick={dismissGuide}>
            All done — close checklist
          </Button>
        ) : (
          <button
            onClick={dismissGuide}
            style={{ ...textButton, fontSize: "0.75rem", textAlign: "center", width: "100%" }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "hsl(var(--foreground))"; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "hsl(var(--muted-foreground))"; }}
          >
            Hide checklist
          </button>
        )}
      </div>
    </div>
  );
}
